"use client";

import Image, { getImageProps, type ImageLoader, type ImageLoaderProps, type ImageProps } from "next/image";
import { useCallback, useState } from "react";
import { placeholderSrc, useDeferredImage, type ImageDefer } from "@/components/DeferredImage";

/**
 * Drop-in replacement for `next/image` that serves + resizes our R2-hosted example
 * assets (/cars/*, /funnel/*) through Cloudflare Image Resizing instead of Next's
 * built-in optimizer. The optimizer runs on the (small, slow) Next server; resizing at
 * Cloudflare's edge keeps the funnel/landing imagery fast.
 *
 * Originals live in R2 under the `vellum/` key prefix, exposed by the cdn host.
 * Host/prefix are overridable via env but default to the real CDN, so no config needed.
 *
 *   NEXT_PUBLIC_IMAGE_CDN        (default https://cdn.vellumprintworks.com)
 *   NEXT_PUBLIC_IMAGE_CDN_PREFIX (default /vellum)
 */
const CDN = (process.env.NEXT_PUBLIC_IMAGE_CDN ?? "https://cdn.vellumprintworks.com").replace(/\/$/, "");
const PREFIX = process.env.NEXT_PUBLIC_IMAGE_CDN_PREFIX ?? "/vellum";

// Hosts that expose the same R2 bucket as the cdn host. Absolute URLs on these hosts are
// resized by key path on the cdn host: cross-origin transformation of files.* is not in the
// zone's allowed-origins list (ERROR 9401), but the same key served via cdn.* resizes fine.
const BUCKET_HOSTS = new Set(["files.vellumprintworks.com", new URL(CDN).hostname]);

function bucketHost(src: string): string | null {
  if (!/^https?:\/\//.test(src)) return null;
  try {
    return new URL(src).hostname;
  } catch {
    return null;
  }
}

function cloudflareLoader({ src, width, quality }: ImageLoaderProps): string {
  // fit=scale-down never upscales past the source resolution; format=auto negotiates avif/webp.
  const opts = `width=${width},quality=${quality ?? 80},format=auto,fit=scale-down`;
  let source: string;
  if (/^https?:\/\//.test(src)) {
    const url = new URL(src);
    // Same-bucket absolute URL → resize by key path (keep the query: RetryImage's
    // cache-buster must reach the origin fetch). Component below guarantees the host matches.
    source = url.pathname + url.search;
  } else {
    // Relative public path (/cars/…, /funnel/…) → resolve against the R2 key prefix.
    source = `${PREFIX}${src}`;
  }
  const sep = source.startsWith("/") ? "" : "/";
  return `${CDN}/cdn-cgi/image/${opts}${sep}${source}`;
}

// A file deployed with the site (public/), resized by Cloudflare at the page's own origin. The zone
// that serves the site has image transformations on (the cdn host is the same zone), so
// /cdn-cgi/image/<opts>/pets/x.webp fetches /pets/x.webp from this deployment and returns the same
// bytes the cdn host returns for its R2 copy. Two reasons to use it over the cdn host or Next's
// default:
//  - On next-on-pages /_next/image is a passthrough: it served the pets lander's 1600px hero, 180 KB,
//    to a 412px phone. Resized here it is ~40 KB.
//  - The request rides the document's own HTTP/2 connection. For the LCP image that is the cdn
//    host's DNS + TCP + TLS setup off the critical path.
// Production builds only: `next dev` has no /cdn-cgi route (and a local `next start` needs a proxy
// that forwards /cdn-cgi/image to the live site).
function siteLoader({ src, width, quality }: ImageLoaderProps): string {
  return `/cdn-cgi/image/width=${width},quality=${quality ?? 80},format=auto,fit=scale-down${src}`;
}

// Assets that live in the app's own public/ folder but are NOT (yet) in R2. These are resized at the
// site's origin (siteLoader) instead of by the R2/CDN loader, so they don't 404 when a file was
// committed to public/ but never uploaded to R2 (e.g. a new style's samples).
const ORIGIN_PREFIXES = [
  "/experiments/",
  "/cars/riviera/",
  "/cars/lage-landen/",
  "/cars/grand-tour/",
  "/cars/circuit-67/",
  "/cars/heritage-press/",
  "/cars/grand-prix-bill/",
  "/cars/auction-bill/",
  "/cars/garage-sign/",
  "/cars/rally-plate/",
  "/cars/billing-block/",
  "/pets/",
];

type CdnImageProps = ImageProps & {
  /** `src` is a public/ file, deployed with the site: resize it at this origin (siteLoader). */
  site?: boolean;
  /** A below-the-fold image: no request until it is near the viewport (see useDeferredImage). */
  defer?: ImageDefer;
};

export default function CdnImage({ site, defer, ...props }: CdnImageProps) {
  const src = typeof props.src === "string" ? props.src : "";
  if (site || ORIGIN_PREFIXES.some((prefix) => src.startsWith(prefix))) {
    return process.env.NODE_ENV === "production" ? <CdnImg {...props} defer={defer} loader={siteLoader} /> : <Image {...props} />;
  }
  // Absolute URLs outside our bucket hosts (localhost previews in dev, mollie icons, …)
  // keep Next's default handling; bucket-hosted ones go through Cloudflare resizing.
  const host = bucketHost(src);
  if (host && !BUCKET_HOSTS.has(host)) {
    return <Image {...props} />;
  }
  return <CdnImg {...props} defer={defer} loader={cloudflareLoader} />;
}

// next/image lists every configured width in srcset (all 16 for a fixed `sizes` such as "76px"),
// each a full /cdn-cgi/image/... URL, and the HTML carries that list for every image: over 100 KB
// of the homepage's markup. This renders the <img> next/image would, without the widths no browser
// can pick for this `sizes` (pickableWidths). Priority images (next/image preloads them) and blur
// placeholders stay on next/image.
function CdnImg({ defer, ...props }: ImageProps & { loader: ImageLoader; defer?: ImageDefer }) {
  const [failed, setFailed] = useState(false);
  const [deferRef, ready] = useDeferredImage(defer);
  const { onError, onLoad, sizes, src } = props;
  // As next/image: an error before hydration had no listener, so set src again once mounted.
  const resetSrc = useCallback(
    (element: HTMLImageElement | null) => {
      deferRef.current = element;
      if (element && onError && ready) element.src = element.src;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- next/image's dependencies
    [src, onError, ready],
  );
  if (props.priority || !sizes || props.unoptimized || (props.placeholder ?? "empty") !== "empty" || props.onLoadingComplete) {
    return <Image {...props} />;
  }
  const { props: img } = getImageProps(props);
  const { srcSet, style, ...attrs } = img;
  if (!srcSet) return <Image {...props} />;
  const candidates = srcSet.split(", ").map((entry) => {
    const at = entry.lastIndexOf(" ");
    return { url: entry.slice(0, at), width: Number(entry.slice(at + 1, -1)) };
  });
  const keep = pickableWidths(
    candidates.map((candidate) => candidate.width),
    sizes,
  );
  const kept = candidates.filter((candidate) => keep.has(candidate.width));
  if (!ready) {
    // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
    return <img {...attrs} ref={resetSrc} src={placeholderSrc(attrs.width, attrs.height)} style={style} />;
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
    <img
      {...attrs}
      onError={(event) => {
        // next/image shows the alt text (drops color: transparent) once the image fails.
        setFailed(true);
        onError?.(event);
      }}
      onLoad={onLoad}
      ref={resetSrc}
      srcSet={kept.map((candidate) => `${candidate.url} ${candidate.width}w`).join(", ")}
      src={kept[kept.length - 1].url}
      style={failed ? { ...style, color: undefined } : style}
    />
  );
}

// Chromium's srcset pick (HTMLSrcsetParser, PickBestImageCandidate) for a slot `slot` CSS px wide
// at device pixel ratio `dpr`, candidates ascending: at the first candidate whose successor covers
// dpr, take the successor if dpr <= 1 and this one falls short of it, or if dpr reaches the
// geometric mean of the two densities; else take this one.
function chromiumPick(widths: number[], slot: number, dpr: number): number {
  let i = 0;
  for (; i < widths.length - 1; i++) {
    const next = widths[i + 1] / slot;
    if (next < dpr) continue;
    const current = widths[i] / slot;
    if ((dpr <= 1 && dpr > current) || dpr >= Math.sqrt(current * next)) i++;
    break;
  }
  return i;
}

// The slot width in CSS px that `sizes` gives at viewport width vw. Handles what this codebase
// writes: (min-width|max-width: Npx) conditions; px, vw and rem lengths.
function slotWidth(sizes: string, vw: number): number {
  for (const part of sizes.split(",")) {
    const match = /^\s*(?:\((min|max)-width:\s*(\d+)px\)\s*)?([\d.]+)(px|vw|rem)\s*$/.exec(part);
    if (!match) return NaN;
    const [, kind, breakpoint, value, unit] = match;
    if (kind === "min" && vw < Number(breakpoint)) continue;
    if (kind === "max" && vw > Number(breakpoint)) continue;
    return unit === "vw" ? (Number(value) * vw) / 100 : unit === "rem" ? Number(value) * 16 : Number(value);
  }
  return NaN;
}

// The widths some screen can pick: everything from the smallest pick upward. The smallest pick is
// at the narrowest slot and the lowest device pixel ratio (viewports from 280 CSS px, ratios from
// 0.75 for zoomed-out browsers; the pick never moves down as the needed pixels grow; the extra
// samples cover the dpr <= 1 branch). Narrower widths are never chosen, so dropping them changes no
// screen's image. Every wider one stays: Chromium takes a wider candidate it already has in memory
// cache (a gallery thumbnail reuses its slide's 640 px file), and that must keep working. An
// unparseable `sizes` keeps every width.
function pickableWidths(widths: number[], sizes: string): Set<number> {
  const viewports = [280, 3840];
  for (const breakpoint of sizes.match(/\d+(?=px\))/g) ?? []) {
    viewports.push(Number(breakpoint) - 1, Number(breakpoint), Number(breakpoint) + 1);
  }
  const slots = viewports.map((vw) => slotWidth(sizes, vw));
  if (slots.some((slot) => !(slot > 0))) return new Set(widths);
  const lo = Math.min(...slots);
  const from = Math.min(chromiumPick(widths, lo, 0.75), chromiumPick(widths, lo, 1), chromiumPick(widths, lo, 1.0001));
  return new Set(widths.slice(from));
}
